/**
 * Lighting setup for the game scene
 * Enhanced with warm Eastern aesthetic lighting
 * Features: golden sunlight, atmospheric sky, time-of-day simulation
 */

import * as THREE from 'three';

// Time of day presets
interface TimeOfDayPreset {
  sunColor: number;
  sunIntensity: number;
  skyColor: number;
  groundColor: number;
  hemisphereIntensity: number;
  ambientColor: number;
  ambientIntensity: number;
  rimColor: number;
  rimIntensity: number;
  fogColor: number;
  sunAngle: number; // Angle from horizon (0-90)
}

const TIME_PRESETS: Record<string, TimeOfDayPreset> = {
  dawn: {
    sunColor: 0xffb366,
    sunIntensity: 0.6,
    skyColor: 0xffd9b3,
    groundColor: 0x4a3520,
    hemisphereIntensity: 0.7,
    ambientColor: 0xffeedd,
    ambientIntensity: 0.2,
    rimColor: 0xff9955,
    rimIntensity: 0.3,
    fogColor: 0xffe8cc,
    sunAngle: 15,
  },
  morning: {
    sunColor: 0xfff5e6,
    sunIntensity: 0.85,
    skyColor: 0xcfe8ff,
    groundColor: 0x3a5a3a,
    hemisphereIntensity: 0.85,
    ambientColor: 0xf5f0e6,
    ambientIntensity: 0.25,
    rimColor: 0xffd700,
    rimIntensity: 0.15,
    fogColor: 0xe8f4ff,
    sunAngle: 45,
  },
  midday: {
    sunColor: 0xfffaf5,
    sunIntensity: 1.0,
    skyColor: 0xb8d4ff,
    groundColor: 0x445544,
    hemisphereIntensity: 0.9,
    ambientColor: 0xffffff,
    ambientIntensity: 0.3,
    rimColor: 0xffeedd,
    rimIntensity: 0.1,
    fogColor: 0xe6f0ff,
    sunAngle: 75,
  },
  afternoon: {
    sunColor: 0xfff0d4,
    sunIntensity: 0.9,
    skyColor: 0xd4e8ff,
    groundColor: 0x4a5540,
    hemisphereIntensity: 0.85,
    ambientColor: 0xfff5e6,
    ambientIntensity: 0.25,
    rimColor: 0xffcc66,
    rimIntensity: 0.2,
    fogColor: 0xf0f5ff,
    sunAngle: 55,
  },
  sunset: {
    sunColor: 0xff8844,
    sunIntensity: 0.7,
    skyColor: 0xff9966,
    groundColor: 0x332211,
    hemisphereIntensity: 0.65,
    ambientColor: 0xffccaa,
    ambientIntensity: 0.2,
    rimColor: 0xff6633,
    rimIntensity: 0.4,
    fogColor: 0xffccaa,
    sunAngle: 10,
  },
};

export class Lights {
  private scene: THREE.Scene;

  // Main lights
  public readonly hemisphereLight: THREE.HemisphereLight;
  public readonly directionalLight: THREE.DirectionalLight;
  public readonly ambientLight: THREE.AmbientLight;

  // Accent lights
  public readonly rimLight: THREE.DirectionalLight;
  public readonly fillLight: THREE.DirectionalLight;

  // Current state
  private currentPreset: string = 'afternoon';
  private transitionProgress: number = 1;
  private targetPreset: string = 'afternoon';
  private time: number = 0;

  // Shadow frustum size (adjustable based on camera zoom)
  private shadowFrustumSize: number = 60;

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // Start with afternoon preset (warm golden light)
    const preset = TIME_PRESETS[this.currentPreset];

    // Hemisphere light - sky/ground gradient for natural ambient
    this.hemisphereLight = new THREE.HemisphereLight(
      preset.skyColor,
      preset.groundColor,
      preset.hemisphereIntensity
    );
    scene.add(this.hemisphereLight);

    // Main directional light (sun)
    this.directionalLight = new THREE.DirectionalLight(preset.sunColor, preset.sunIntensity);
    this.setupSunPosition(preset.sunAngle);
    this.setupShadows();
    scene.add(this.directionalLight);
    scene.add(this.directionalLight.target);

    // Rim light - warm accent from opposite direction
    this.rimLight = new THREE.DirectionalLight(preset.rimColor, preset.rimIntensity);
    this.rimLight.position.set(-30, 15, -30);
    scene.add(this.rimLight);

    // Fill light - soft blue from below/front for shadow fill
    this.fillLight = new THREE.DirectionalLight(0x8899bb, 0.15);
    this.fillLight.position.set(0, -5, 20);
    scene.add(this.fillLight);

    // Ambient light - general fill
    this.ambientLight = new THREE.AmbientLight(preset.ambientColor, preset.ambientIntensity);
    scene.add(this.ambientLight);

    // Set initial fog
    this.updateFog(preset.fogColor);
  }

  private setupSunPosition(angle: number): void {
    const radians = (angle * Math.PI) / 180;
    const distance = 50;

    // Sun comes from the "east" direction with some north offset
    const x = Math.cos(radians) * distance * 0.7;
    const y = Math.sin(radians) * distance;
    const z = Math.cos(radians) * distance * 0.5;

    this.directionalLight.position.set(x, y, z);
  }

  private setupShadows(): void {
    this.directionalLight.castShadow = true;

    // High quality shadow map
    this.directionalLight.shadow.mapSize.set(2048, 2048);

    // Soft shadows with PCF
    this.directionalLight.shadow.radius = 2;

    // Shadow bias to prevent acne
    this.directionalLight.shadow.bias = -0.0003;
    this.directionalLight.shadow.normalBias = 0.02;

    // Shadow camera setup
    const shadowCam = this.directionalLight.shadow.camera;
    shadowCam.near = 1;
    shadowCam.far = 150;
    shadowCam.left = -this.shadowFrustumSize;
    shadowCam.right = this.shadowFrustumSize;
    shadowCam.top = this.shadowFrustumSize;
    shadowCam.bottom = -this.shadowFrustumSize;
  }

  private updateFog(color: number): void {
    // Exponential fog for atmosphere
    if (!this.scene.fog) {
      this.scene.fog = new THREE.FogExp2(color, 0.008);
    } else if (this.scene.fog instanceof THREE.FogExp2) {
      this.scene.fog.color.setHex(color);
    }
  }

  /**
   * Set time of day preset
   */
  setTimeOfDay(preset: keyof typeof TIME_PRESETS): void {
    if (preset === this.currentPreset) return;

    this.targetPreset = preset;
    this.transitionProgress = 0;
  }

  /**
   * Smoothly interpolate between presets
   */
  private lerpPresets(from: TimeOfDayPreset, to: TimeOfDayPreset, t: number): void {
    // Smooth easing
    const eased = t * t * (3 - 2 * t);

    // Sun
    this.directionalLight.color.lerpColors(
      new THREE.Color(from.sunColor),
      new THREE.Color(to.sunColor),
      eased
    );
    this.directionalLight.intensity = THREE.MathUtils.lerp(from.sunIntensity, to.sunIntensity, eased);

    // Sun angle
    const angle = THREE.MathUtils.lerp(from.sunAngle, to.sunAngle, eased);
    this.setupSunPosition(angle);

    // Hemisphere
    this.hemisphereLight.color.lerpColors(
      new THREE.Color(from.skyColor),
      new THREE.Color(to.skyColor),
      eased
    );
    this.hemisphereLight.groundColor.lerpColors(
      new THREE.Color(from.groundColor),
      new THREE.Color(to.groundColor),
      eased
    );
    this.hemisphereLight.intensity = THREE.MathUtils.lerp(
      from.hemisphereIntensity,
      to.hemisphereIntensity,
      eased
    );

    // Ambient
    this.ambientLight.color.lerpColors(
      new THREE.Color(from.ambientColor),
      new THREE.Color(to.ambientColor),
      eased
    );
    this.ambientLight.intensity = THREE.MathUtils.lerp(from.ambientIntensity, to.ambientIntensity, eased);

    // Rim light
    this.rimLight.color.lerpColors(
      new THREE.Color(from.rimColor),
      new THREE.Color(to.rimColor),
      eased
    );
    this.rimLight.intensity = THREE.MathUtils.lerp(from.rimIntensity, to.rimIntensity, eased);

    // Fog
    const fogColor = new THREE.Color(from.fogColor).lerp(new THREE.Color(to.fogColor), eased);
    this.updateFog(fogColor.getHex());
  }

  /**
   * Update shadow camera to follow the game camera
   */
  updateShadowCamera(centerX: number, centerZ: number): void {
    const preset = TIME_PRESETS[this.currentPreset];
    const radians = (preset.sunAngle * Math.PI) / 180;
    const distance = 50;

    const offsetX = Math.cos(radians) * distance * 0.7;
    const offsetY = Math.sin(radians) * distance;
    const offsetZ = Math.cos(radians) * distance * 0.5;

    this.directionalLight.position.set(centerX + offsetX, offsetY, centerZ + offsetZ);
    this.directionalLight.target.position.set(centerX, 0, centerZ);
    this.directionalLight.target.updateMatrixWorld();

    // Update shadow camera projection
    this.directionalLight.shadow.camera.updateProjectionMatrix();
  }

  /**
   * Adjust shadow quality based on distance
   */
  setShadowQuality(quality: 'low' | 'medium' | 'high' | 'ultra'): void {
    const sizes = {
      low: 512,
      medium: 1024,
      high: 2048,
      ultra: 4096,
    };

    const frustumSizes = {
      low: 80,
      medium: 70,
      high: 60,
      ultra: 50,
    };

    this.directionalLight.shadow.mapSize.set(sizes[quality], sizes[quality]);
    this.shadowFrustumSize = frustumSizes[quality];

    const shadowCam = this.directionalLight.shadow.camera;
    shadowCam.left = -this.shadowFrustumSize;
    shadowCam.right = this.shadowFrustumSize;
    shadowCam.top = this.shadowFrustumSize;
    shadowCam.bottom = -this.shadowFrustumSize;
    shadowCam.updateProjectionMatrix();

    // Need to recreate shadow map when size changes
    this.directionalLight.shadow.map?.dispose();
    this.directionalLight.shadow.map = null as unknown as THREE.WebGLRenderTarget;
  }

  /**
   * Enable/disable shadows for performance
   */
  setShadowsEnabled(enabled: boolean): void {
    this.directionalLight.castShadow = enabled;
  }

  /**
   * Update lighting (call each frame for transitions)
   */
  update(deltaTime: number): void {
    this.time += deltaTime;

    // Handle preset transitions
    if (this.transitionProgress < 1 && this.targetPreset !== this.currentPreset) {
      this.transitionProgress += deltaTime * 0.5; // 2 second transition
      if (this.transitionProgress >= 1) {
        this.transitionProgress = 1;
        this.currentPreset = this.targetPreset;
      }

      const fromPreset = TIME_PRESETS[this.currentPreset];
      const toPreset = TIME_PRESETS[this.targetPreset];
      this.lerpPresets(fromPreset, toPreset, this.transitionProgress);
    }

    // Subtle light flickering for atmosphere
    const flicker = Math.sin(this.time * 0.5) * 0.02 + Math.sin(this.time * 1.3) * 0.01;
    const baseIntensity = TIME_PRESETS[this.currentPreset].sunIntensity;
    this.directionalLight.intensity = baseIntensity + flicker;
  }

  /**
   * Get current time of day preset name
   */
  getCurrentPreset(): string {
    return this.currentPreset;
  }

  /**
   * Get available presets
   */
  static getAvailablePresets(): string[] {
    return Object.keys(TIME_PRESETS);
  }
}
